import historicalData from "./historicalData";
import ratesData from "./ratesData";

// Derive the FAQ's threshold prose from the single-sourced data files so it
// can never go stale independently of historicalData.js / ratesData.js.
const floors = historicalData.defaultPlanFloor;

// GOV.UK presents weekly/monthly figures as the floor of the annual
// threshold divided by 52/12 respectively.
const weekly = (value) => Math.floor(value / 52).toLocaleString("en-GB");
const monthly = (value) => Math.floor(value / 12).toLocaleString("en-GB");
const annual = (value) => value.toLocaleString("en-GB");

const [taxYearStartStr] = ratesData.taxYear.split("/");
const taxYearStart = parseInt(taxYearStartStr, 10);
const taxYearEnd = taxYearStart + 1;

const faqData = {
  howDoesItWork: {
    tags: [],
    title: "How does the calculator work?",
    content: (
      <>
        <br />
        <p>
          If you are a student with a plan 1, 2, 4, 5 or postgraduate student
          loan, you only make repayments if your pre-tax salary is above the
          repayment threshold. Your repayment totals 9% of your earnings above
          the threshold on plan 1, 2, 4 or 5 or 6% of your earnings above the
          threshold if you are on a postgraduate plan.
        </p>
        <p>
          This calculator uses the inputs about your student loan debt and a
          number of assumptions to calculate your repayments each year. The
          calculator adjusts for future increases in your income, changes to the
          thresholds and the interest that accrues on your loan.
        </p>
      </>
    ),
  },

  whenDoYouRepay: {
    tags: [],
    title: "When do you start repaying?",
    content: (
      <>
        The earliest you’ll start repaying is either:
        <br />
        <br />
        <ul>
          <li>The April after you leave your course</li>
          <li>
            The April 4 years after the course started, if you’re studying
            part-time
          </li>
        </ul>
        You only make repayments if you earn more than the salary threshold for
        your loan plan. This means your repayments automatically stop if either:
        <br />
        <br />
        <ul>
          <li>You stop working</li>
          <li>Your income goes below the threshold</li>
          <li>You repay your student loan balance</li>
          <li>Your student loan balance is written off</li>
        </ul>
      </>
    ),
    reference: "https://www.gov.uk/repaying-your-student-loan",
  },

  plan1loans: {
    tags: [],
    title: "What are Plan 1 loans?",
    content: (
      <>
        Plan 1 Loans are a type of UK student loan. You’re on Plan 1 if you’re:
        <br />
        <br />
        <ul>
          <li>
            An English or Welsh student who started an undergraduate course
            anywhere in the UK before 1 September 2012
          </li>
          <li>
            A Northern Irish student who started an undergraduate or
            postgraduate course anywhere in the UK on or after 1 September 1998.
            Northern Ireland still uses Plan 1 loans.
          </li>
          <li>
            An EU student who started an undergraduate course in England or
            Wales on or after 1 September 1998, but before 1 September 2012
          </li>
          <li>
            An EU student who started an undergraduate or postgraduate course in
            Northern Ireland on or after 1 September 1998
          </li>
        </ul>
      </>
    ),
    reference: "https://www.gov.uk/repaying-your-student-loan",
  },

  plan2loans: {
    tags: [],
    title: "What are Plan 2 loans?",
    content: (
      <>
        Plan 2 loans are a type of UK student loan. You’re on
        Plan 2 if you’re:
        <br />
        <br />
        <ul>
          <li>
            A Student Finance England funded student who started an
            undergraduate course between 1 September 2012 and 31 July 2023
          </li>
          <li>
            A Student Finance Wales funded student who started an undergraduate
            course on or after 1 September 2012
          </li>
          <li>
            Someone who took out a Student Finance England Advanced Learner
            Loan before 1 August 2023
          </li>
          <li>
            Other exceptions apply. See the full{" "}
            <a href="https://www.gov.uk/repaying-your-student-loan/which-repayment-plan-you-are-on">
              GOV.UK eligibility guidance
            </a>
            .
          </li>
        </ul>
      </>
    ),
    reference: "https://www.gov.uk/repaying-your-student-loan/which-repayment-plan-you-are-on",
  },

  plan4loans: {
    tags: [],
    title: "What are Plan 4 loans?",
    content: (
      <>
        Plan 4 loans are for eligible students funded through the Student
        Awards Agency Scotland (SAAS). You’re on Plan 4 if you’re:
        <br />
        <br />
        <ul>
          <li>
            A Scottish student funded through SAAS who started an undergraduate
            or postgraduate course anywhere in the UK on or after 1 September
            1998
          </li>
          <li>
            An eligible EU student funded through SAAS who started an
            undergraduate or postgraduate course in Scotland on or after 1
            September 1998
          </li>
        </ul>
      </>
    ),
    reference: "https://www.gov.uk/repaying-your-student-loan/which-repayment-plan-you-are-on",
  },

  plan5loans: {
    tags: [],
    title: "What are Plan 5 loans?",
    content: (
      <>
        Plan 5 Loans are a newer type of UK student loan introduced in 2023, for
        English Students. You’re on Plan 5 if you’re:
        <br />
        <br />
        <ul>
          <li>
            A student funded by Student Finance England who started an
            undergraduate or PGCE course, or an Advanced Learner Loan, on or
            after 1 August 2023
          </li>
        </ul>
      </>
    ),
    reference: "https://www.gov.uk/repaying-your-student-loan",
  },

  repaymentThresholds: {
    tags: [],
    title: "What are the repayment thresholds?",
    content: (
      <>
        You only make repayments towards your student loan balance if your
        salary is above the repayment threshold. The threshold depends on the
        loan plan you are on and the thresholds are subject to change every
        year. The following thresholds are correct for the {ratesData.taxYear}{" "}
        tax year (6 April {taxYearStart} to 5 April {taxYearEnd}).
        <br />
        <br />
        <b>If you have a Plan 1 student loan: </b>
        <br />
        You’ll only repay when your income is over £{weekly(floors.plan1)} a
        week, £{monthly(floors.plan1)} a month or £{annual(floors.plan1)} a
        year (before tax and other deductions). <br />
        <br />
        <b>If you have a Plan 2 student loan:</b>
        <br />
        You’ll only repay when your income is over £{weekly(floors.plan2)} a
        week, £{monthly(floors.plan2)} a month or £{annual(floors.plan2)} a
        year (before tax and other deductions). <br />
        <br />
        <b>If you have a Plan 4 student loan:</b>
        <br />
        You’ll only repay when your income is over £{weekly(floors.plan4)} a
        week, £{monthly(floors.plan4)} a month or £{annual(floors.plan4)} a
        year (before tax and other deductions).
        <br /><br />
        <b>If you have a Plan 5 student loan:</b>
        <br />
        You’ll only repay when your income is over £{weekly(floors.plan5)} a
        week, £{monthly(floors.plan5)} a month or £{annual(floors.plan5)} a
        year (before tax and other deductions).
        <br />
        <br /> <b>If you’re on a Postgraduate Loan repayment plan:</b>
        <br /> If you took out a Master’s Loan or a Doctoral Loan, you’ll only
        repay when your income is over £{weekly(floors.planPostgrad)} a week,
        £{monthly(floors.planPostgrad)} a month or £
        {annual(floors.planPostgrad)} a year (before tax and other
        deductions).
      </>
    ),
    reference: "https://www.gov.uk/repaying-your-student-loan",
  },
  loanVsGrant: {
    tags: [],
    title: "What is the difference between a loan and a grant/bursary?",
    content: (
      <>
        You only make repayments on:
        <br />
        <br />
        <ul>
          <li>Tuition Fee Loans</li>
          <li>Maintenance Loans</li>
          <li>Postgraduate Loans</li>
        </ul>
        You don't need to make repayments for grants or bursaries.
      </>
    ),
    reference: "https://www.gov.uk/repaying-your-student-loan",
  },

  checkBalance: {
    tags: [],
    title: "How do I check my student loan balance?",
    content: (
      <>
        Use the official GOV.UK student loan repayment account to check your
        balance and plan. After your course has finished, enter a balance dated
        in the current calendar year in the calculator and select its month.
        Keep each repayment plan's balance separate and use Add another loan
        if you have more than one plan. Our{" "}
        <a href="/guides/check-student-loan-balance" target="_blank" rel="noopener noreferrer">balance-checking guide (opens in a new tab)</a>{" "}
        explains the steps and date limits. This site does not sign in to SLC
        or retrieve your account details.
      </>
    ),
    reference: "https://www.gov.uk/sign-in-to-manage-your-student-loan-balance",
  },

  repayQuickly: {
    tags: [],
    title: "Can I repay more quickly?",
    content: (
      <>
        You can make extra repayments at any time without a penalty, but they
        cannot be refunded. They may not save money if your balance would be
        written off before you repay it in full, so use the calculator’s
        overpayment scenario to compare the outcomes.
      </>
    ),
    reference: "https://www.gov.uk/repaying-your-student-loan/make-extra-repayments",
  },

  shackledWithDebt: {
    tags: [],
    title: "When is the remaining loan written off?",
    content: (
      <>
        For plans with a year-based write-off, the term starts from the April
        when you were first due to repay. Plan 2 loans are written off after 30 years, Plan 5 loans after
        40 years, and Postgraduate Loans in England and Wales after 30 years.
        Plan 1 and Plan 4 depend on when you first received the loan. See the
        official guidance for the dates and exceptions.
      </>
    ),
    reference: "https://www.gov.uk/repaying-your-student-loan/when-your-student-loan-gets-written-off-or-cancelled",
  },
};

export default faqData;
